/**
 * Settings Controller Module
 * Handles Settings page initialization and AI backend configuration
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only settings page logic
 * - Composition: Contains AIBackendSettingsController
 */

import { PageName } from '../types/page-types';
import { BaseController, ControllerServices } from './base-controller';
import { isMainnet } from '../../config/environment';

/**
 * LLM Endpoint structure
 */
interface LLMEndpoint {
    name: string;
    url: string;
    model_name: string;
    backend_type: string;
    enabled: boolean;
    max_tokens: number;
    timeout_seconds: number;
}

/**
 * AIBackendSettingsController class
 * Manages AI backend settings (Local, Claude, Self-Hosted LLM)
 */
export class AIBackendSettingsController {
    private services: ControllerServices;

    constructor(services: ControllerServices) {
        this.services = services;
    }

    private get canisterClient(): any {
        return this.services.canisterClient || (window as any).canisterClient;
    }

    private get settingsService(): any {
        return this.services.settingsService;
    }

    /**
     * Initialize AI backend settings
     */
    public async init(): Promise<void> {
        const localRadio = document.getElementById('ai-backend-local') as HTMLInputElement;
        const claudeRadio = document.getElementById('ai-backend-claude') as HTMLInputElement;
        const llmRadio = document.getElementById('ai-backend-llm') as HTMLInputElement;
        const claudeConfigSection = document.getElementById('claude-config-section');
        const llmConfigSection = document.getElementById('llm-config-section');
        const configureButton = document.getElementById('configure-claude-api-key');
        const claudeStatus = document.getElementById('claude-status');
        const manageLLMButton = document.getElementById('manage-llm-endpoints');
        const testLLMButton = document.getElementById('test-llm-connection');

        if (!localRadio || !claudeRadio || !llmRadio) {
            console.error('AI backend radio buttons not found');
            return;
        }

        // Disable Self-Hosted LLM option on mainnet (requires local proxy)
        if (isMainnet()) {
            llmRadio.disabled = true;
            const llmLabel = llmRadio.closest('label');
            if (llmLabel) {
                llmLabel.classList.add('opacity-50', 'cursor-not-allowed');
                llmLabel.title = 'Self-Hosted LLM requires local proxy server (not available on mainnet)';
                // Add visual indicator
                const descDiv = llmLabel.querySelector('.text-xs');
                if (descDiv) {
                    descDiv.innerHTML = '<span class="text-orange-500">Not available on mainnet</span> - Requires local proxy';
                }
            }
        }

        // Load saved setting
        let savedBackend = this.settingsService.getAiBackend();

        // If on mainnet and LLM was selected, fall back to local
        if (isMainnet() && savedBackend === 'llm') {
            savedBackend = 'local';
            this.settingsService.setAiBackend('local');
            console.warn('Self-Hosted LLM not available on mainnet, falling back to Local Patterns');
        }
        this.applyBackendSelection(savedBackend, localRadio, claudeRadio, llmRadio, claudeConfigSection, llmConfigSection);

        // Check Claude configuration status
        await this.checkClaudeStatus(claudeStatus);

        // Check LLM configuration status
        await this.updateLLMStatus();

        // Setup radio button handlers
        this.setupRadioHandlers(localRadio, claudeRadio, llmRadio, claudeConfigSection, llmConfigSection);

        // Setup Claude API key button
        if (configureButton) {
            configureButton.addEventListener('click', () => this.configureClaudeApiKey(claudeStatus));
        }

        // Setup LLM buttons
        const addLLMButton = document.getElementById('add-llm-endpoint');
        if (addLLMButton) {
            addLLMButton.addEventListener('click', () => this.showAddLLMEndpointModal());
        }

        if (manageLLMButton) {
            manageLLMButton.addEventListener('click', () => this.showLLMEndpointsListModal());
        }

        if (testLLMButton) {
            testLLMButton.addEventListener('click', () => this.testLLMEndpoints());
        }

        // Expose methods to window for modal callbacks
        (window as any).showAddLLMEndpointModal = () => this.showAddLLMEndpointModal();
        (window as any).hideLLMEndpointModal = () => this.hideLLMEndpointModal();
        (window as any).saveLLMEndpoint = () => this.saveLLMEndpoint();
        (window as any).testLLMEndpointConnection = () => this.testLLMEndpointConnection();
        (window as any).fetchLLMModels = () => this.fetchLLMModels();
        (window as any).showLLMEndpointsListModal = () => this.showLLMEndpointsListModal();
        (window as any).hideLLMEndpointsListModal = () => this.hideLLMEndpointsListModal();
        (window as any).editLLMEndpoint = (name: string) => this.editLLMEndpoint(name);
        (window as any).deleteLLMEndpoint = (name: string) => this.deleteLLMEndpoint(name);
        (window as any).toggleLLMEndpoint = (name: string, enabled: boolean) => this.toggleLLMEndpoint(name, enabled);
    }

    /**
     * Apply backend selection to UI
     */
    private applyBackendSelection(
        savedBackend: string,
        localRadio: HTMLInputElement,
        claudeRadio: HTMLInputElement,
        llmRadio: HTMLInputElement,
        claudeConfigSection: HTMLElement | null,
        llmConfigSection: HTMLElement | null
    ): void {
        if (savedBackend === 'claude') {
            claudeRadio.checked = true;
            if (claudeConfigSection) claudeConfigSection.classList.remove('hidden');
            if (llmConfigSection) llmConfigSection.classList.add('hidden');
        } else if (savedBackend === 'llm') {
            llmRadio.checked = true;
            if (claudeConfigSection) claudeConfigSection.classList.add('hidden');
            if (llmConfigSection) llmConfigSection.classList.remove('hidden');
        } else {
            localRadio.checked = true;
            if (claudeConfigSection) claudeConfigSection.classList.add('hidden');
            if (llmConfigSection) llmConfigSection.classList.add('hidden');
        }
    }

    /**
     * Check Claude configuration status
     */
    private async checkClaudeStatus(claudeStatus: HTMLElement | null): Promise<void> {
        try {
            const isConfigured = await this.canisterClient.isClaudeConfigured();
            if (claudeStatus) {
                claudeStatus.textContent = isConfigured ? '✓ API Key Configured' : '⚠ API Key Required';
                claudeStatus.className = isConfigured ? 'ml-2 text-sm text-green-600' : 'ml-2 text-sm text-orange-600';
            }
        } catch (error) {
            console.error('Error checking Claude configuration:', error);
        }
    }

    /**
     * Setup radio button change handlers
     */
    private setupRadioHandlers(
        localRadio: HTMLInputElement,
        claudeRadio: HTMLInputElement,
        llmRadio: HTMLInputElement,
        claudeConfigSection: HTMLElement | null,
        llmConfigSection: HTMLElement | null
    ): void {
        const handleRadioChange = () => {
            let selectedBackend = 'local';
            if (claudeRadio.checked) selectedBackend = 'claude';
            else if (llmRadio.checked) selectedBackend = 'llm';

            this.settingsService.setAiBackend(selectedBackend);

            if (claudeConfigSection) {
                claudeConfigSection.classList.toggle('hidden', selectedBackend !== 'claude');
            }

            if (llmConfigSection) {
                llmConfigSection.classList.toggle('hidden', selectedBackend !== 'llm');
            }

            console.log(`AI Backend changed to: ${selectedBackend}`);
        };

        localRadio.addEventListener('change', handleRadioChange);
        claudeRadio.addEventListener('change', handleRadioChange);
        llmRadio.addEventListener('change', handleRadioChange);
    }

    /**
     * Configure Claude API key
     */
    private async configureClaudeApiKey(claudeStatus: HTMLElement | null): Promise<void> {
        const apiKey = prompt('Enter your Anthropic API key:\n\n(Get one at: https://console.anthropic.com/settings/keys)');

        if (!apiKey) {
            return;
        }

        try {
            await this.canisterClient.setClaudeApiKey(apiKey);
            alert('✓ API key configured successfully!');

            if (claudeStatus) {
                claudeStatus.textContent = '✓ API Key Configured';
                claudeStatus.className = 'ml-2 text-sm text-green-600';
            }
        } catch (error) {
            console.error('Error setting API key:', error);
            alert('❌ Failed to configure API key. Please try again.');
        }
    }

    /**
     * Update LLM configuration status display
     */
    public async updateLLMStatus(): Promise<void> {
        // Global (always visible) elements
        const endpointCountGlobal = document.getElementById('llm-endpoint-count-global');
        const configStatusGlobal = document.getElementById('llm-config-status-global');

        try {
            const endpointsResult = await this.canisterClient.getLLMEndpoints();

            if (endpointsResult && 'Ok' in endpointsResult) {
                const endpoints: LLMEndpoint[] = endpointsResult.Ok;
                const enabledCount = endpoints.filter(e => e.enabled).length;

                // Update global display
                if (endpointCountGlobal) {
                    if (endpoints.length === 0) {
                        endpointCountGlobal.textContent = 'No endpoints configured';
                    } else {
                        endpointCountGlobal.textContent = `${endpoints.length} endpoint${endpoints.length !== 1 ? 's' : ''} (${enabledCount} enabled)`;
                    }
                }

                if (configStatusGlobal) {
                    if (enabledCount > 0) {
                        configStatusGlobal.textContent = '✓ Ready';
                        configStatusGlobal.className = 'text-sm text-green-600 font-medium';
                    } else if (endpoints.length > 0) {
                        configStatusGlobal.textContent = '⚠ None enabled';
                        configStatusGlobal.className = 'text-sm text-orange-600 font-medium';
                    } else {
                        configStatusGlobal.textContent = '';
                    }
                }
            }
        } catch (error) {
            console.error('Error updating LLM status:', error);
        }
    }

    /**
     * Show LLM endpoint management dialog
     */
    public async showLLMEndpointManager(): Promise<void> {
        try {
            const endpointsResult = await this.canisterClient.getLLMEndpoints();

            if (!endpointsResult || !('Ok' in endpointsResult)) {
                alert('Failed to load LLM endpoints');
                return;
            }

            const endpoints: LLMEndpoint[] = endpointsResult.Ok;

            let message = '=== Self-Hosted LLM Endpoints ===\n\n';

            if (endpoints.length === 0) {
                message += 'No endpoints configured.\n\n';
                message += 'To add an endpoint, you need to:\n';
                message += '1. Deploy a self-hosted LLM (DeepSeek, Llama, Mistral)\n';
                message += '2. Expose it via OpenAI-compatible API\n';
                message += '3. Add the endpoint URL here\n\n';
                message += 'Example: http://your-server:8000';
            } else {
                endpoints.forEach((ep, idx) => {
                    message += `${idx + 1}. ${ep.name}\n`;
                    message += `   URL: ${ep.url}\n`;
                    message += `   Model: ${ep.model_name}\n`;
                    message += `   Type: ${ep.backend_type}\n`;
                    message += `   Status: ${ep.enabled ? '✓ Enabled' : '✗ Disabled'}\n`;
                    message += `   Max Tokens: ${ep.max_tokens}\n`;
                    message += `   Timeout: ${ep.timeout_seconds}s\n\n`;
                });
            }

            alert(message);
            await this.updateLLMStatus();
        } catch (error) {
            console.error('Error showing LLM endpoint manager:', error);
            alert('Failed to load endpoint configuration');
        }
    }

    /**
     * Test LLM endpoint connectivity
     */
    public async testLLMEndpoints(): Promise<void> {
        try {
            const endpointsResult = await this.canisterClient.getLLMEndpoints();

            if (!endpointsResult || !('Ok' in endpointsResult)) {
                alert('Failed to load LLM endpoints');
                return;
            }

            const endpoints: LLMEndpoint[] = endpointsResult.Ok.filter((e: LLMEndpoint) => e.enabled);

            if (endpoints.length === 0) {
                alert('No enabled endpoints to test.\n\nPlease configure and enable at least one LLM endpoint.');
                return;
            }

            let results = '=== LLM Endpoint Test Results ===\n\n';

            for (const endpoint of endpoints) {
                try {
                    const result = await this.canisterClient.testLLMEndpoint(endpoint.name);
                    results += `${result}\n`;
                } catch (error) {
                    results += `❌ ${endpoint.name}: Test failed - ${error}\n`;
                }
            }

            alert(results);
        } catch (error) {
            console.error('Error testing LLM endpoints:', error);
            alert('Failed to test endpoints');
        }
    }

    // Track if we're editing an existing endpoint
    private editingEndpointName: string | null = null;

    /**
     * Show the Add LLM Endpoint modal
     */
    public showAddLLMEndpointModal(endpoint?: LLMEndpoint): void {
        const modal = document.getElementById('add-llm-endpoint-modal');
        const title = document.getElementById('llm-modal-title');
        const saveBtn = document.getElementById('save-llm-btn-text');

        if (!modal) {
            console.error('Add LLM endpoint modal not found');
            return;
        }

        // Reset form
        const form = document.getElementById('add-llm-endpoint-form') as HTMLFormElement;
        if (form) form.reset();

        // Clear test result
        const testResult = document.getElementById('llm-test-result');
        if (testResult) {
            testResult.classList.add('hidden');
            testResult.innerHTML = '';
        }

        // Reset model dropdown
        const modelSelect = document.getElementById('llm-model-name-select') as HTMLSelectElement;
        if (modelSelect) {
            modelSelect.innerHTML = '<option value="">-- Select URL first to load models --</option>';
        }

        if (endpoint) {
            // Edit mode
            this.editingEndpointName = endpoint.name;
            if (title) title.textContent = 'Edit LLM Endpoint';
            if (saveBtn) saveBtn.textContent = 'Save Changes';

            // Populate form
            (document.getElementById('llm-endpoint-name') as HTMLInputElement).value = endpoint.name;
            (document.getElementById('llm-endpoint-url') as HTMLInputElement).value = endpoint.url;
            (document.getElementById('llm-model-name') as HTMLInputElement).value = endpoint.model_name;
            (document.getElementById('llm-backend-type') as HTMLSelectElement).value = endpoint.backend_type;
            (document.getElementById('llm-max-tokens') as HTMLInputElement).value = endpoint.max_tokens.toString();
            (document.getElementById('llm-timeout') as HTMLInputElement).value = endpoint.timeout_seconds.toString();
            (document.getElementById('llm-enabled') as HTMLInputElement).checked = endpoint.enabled;
        } else {
            // Add mode
            this.editingEndpointName = null;
            if (title) title.textContent = 'Add Custom LLM Endpoint';
            if (saveBtn) saveBtn.textContent = 'Add Endpoint';
        }

        modal.classList.remove('hidden');

        // Re-init Feather icons
        if ((window as any).feather) {
            (window as any).feather.replace();
        }
    }

    /**
     * Hide the Add LLM Endpoint modal
     */
    public hideLLMEndpointModal(): void {
        const modal = document.getElementById('add-llm-endpoint-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
        this.editingEndpointName = null;
    }

    /**
     * Save LLM endpoint from form
     */
    public async saveLLMEndpoint(): Promise<void> {
        const name = (document.getElementById('llm-endpoint-name') as HTMLInputElement).value.trim();
        const url = (document.getElementById('llm-endpoint-url') as HTMLInputElement).value.trim();
        const modelName = (document.getElementById('llm-model-name') as HTMLInputElement).value.trim();
        const backendType = (document.getElementById('llm-backend-type') as HTMLSelectElement).value;
        const maxTokens = parseInt((document.getElementById('llm-max-tokens') as HTMLInputElement).value) || 4096;
        const timeout = parseInt((document.getElementById('llm-timeout') as HTMLInputElement).value) || 60;
        const enabled = (document.getElementById('llm-enabled') as HTMLInputElement).checked;

        // Validation
        if (!name || !url || !modelName || !backendType) {
            this.showTestResult('Please fill in all required fields.', false);
            return;
        }

        const endpoint: LLMEndpoint = {
            name,
            url,
            model_name: modelName,
            backend_type: backendType,
            enabled,
            max_tokens: maxTokens,
            timeout_seconds: timeout
        };

        try {
            if (this.editingEndpointName) {
                await this.canisterClient.updateLLMEndpoint(endpoint);
            } else {
                await this.canisterClient.addLLMEndpoint(endpoint);
            }

            // Both methods return void — if no exception, it succeeded
            this.showTestResult(
                this.editingEndpointName
                    ? `Endpoint "${name}" updated successfully!`
                    : `Endpoint "${name}" added successfully!`,
                true
            );

            // Update status and close modal after a short delay
            setTimeout(async () => {
                this.hideLLMEndpointModal();
                await this.updateLLMStatus();
            }, 1500);
        } catch (error) {
            console.error('Error saving LLM endpoint:', error);
            this.showTestResult(`Error: ${error}`, false);
        }
    }

    /**
     * Test LLM endpoint connection from modal
     */
    public async testLLMEndpointConnection(): Promise<void> {
        const url = (document.getElementById('llm-endpoint-url') as HTMLInputElement).value.trim();
        const modelName = (document.getElementById('llm-model-name') as HTMLInputElement).value.trim();

        if (!url) {
            this.showTestResult('Please enter an endpoint URL first.', false);
            return;
        }

        this.showTestResult('Testing connection...', true, true);

        try {
            // Route through proxy server to avoid CORS
            const { environment } = await import('../../config/environment');
            const resp = await fetch(`${environment.proxyUrl}/llm/test-url`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url, model: modelName || 'test' }),
            });
            const result = await resp.json();
            if (result.Ok) {
                this.showTestResult(result.Ok, true);
            } else {
                this.showTestResult(result.Err || 'Connection failed', false);
            }
        } catch (error: any) {
            this.showTestResult(`Connection test failed: ${error.message || error}`, false);
        }
    }

    /**
     * Fetch available models from the endpoint URL and populate the dropdown
     */
    public async fetchLLMModels(): Promise<void> {
        const url = (document.getElementById('llm-endpoint-url') as HTMLInputElement).value.trim();
        const select = document.getElementById('llm-model-name-select') as HTMLSelectElement;
        const modelInput = document.getElementById('llm-model-name') as HTMLInputElement;
        const fetchBtn = document.getElementById('fetch-models-btn') as HTMLButtonElement;

        if (!url) {
            this.showTestResult('Please enter an endpoint URL first.', false);
            return;
        }

        if (!select) return;

        // Show loading state
        select.innerHTML = '<option value="">Loading models...</option>';
        if (fetchBtn) fetchBtn.disabled = true;

        try {
            const { environment } = await import('../../config/environment');
            const resp = await fetch(`${environment.proxyUrl}/llm/list-models`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url }),
            });
            const result = await resp.json();

            if (result.Ok) {
                const models = result.Ok.models || [];
                select.innerHTML = '<option value="">-- Select a model --</option>';
                for (const m of models) {
                    const opt = document.createElement('option');
                    opt.value = m.id;
                    opt.textContent = m.size ? `${m.name} (${m.size})` : m.name;
                    select.appendChild(opt);
                }
                // Auto-select first model if only one or if input is empty
                if (models.length === 1 || (models.length > 0 && !modelInput.value)) {
                    select.value = models[0].id;
                    modelInput.value = models[0].id;
                }
            } else {
                select.innerHTML = '<option value="">-- Failed to load models --</option>';
                this.showTestResult(result.Err || 'Could not fetch models', false);
            }
        } catch (error: any) {
            select.innerHTML = '<option value="">-- Error fetching models --</option>';
            this.showTestResult(`Failed to fetch models: ${error.message || error}`, false);
        } finally {
            if (fetchBtn) fetchBtn.disabled = false;
        }
    }

    /**
     * Show test result in modal
     */
    private showTestResult(message: string, success: boolean, loading = false): void {
        const resultDiv = document.getElementById('llm-test-result');
        if (!resultDiv) return;

        resultDiv.classList.remove('hidden', 'bg-green-50', 'bg-red-50', 'bg-blue-50', 'border-green-200', 'border-red-200', 'border-blue-200');

        if (loading) {
            resultDiv.classList.add('bg-blue-50', 'border', 'border-blue-200');
            resultDiv.innerHTML = `<span class="text-blue-700">${message}</span>`;
        } else if (success) {
            resultDiv.classList.add('bg-green-50', 'border', 'border-green-200');
            resultDiv.innerHTML = `<span class="text-green-700">${message}</span>`;
        } else {
            resultDiv.classList.add('bg-red-50', 'border', 'border-red-200');
            resultDiv.innerHTML = `<span class="text-red-700">${message}</span>`;
        }
    }

    /**
     * Show LLM endpoints list modal
     */
    public async showLLMEndpointsListModal(): Promise<void> {
        const modal = document.getElementById('llm-endpoints-list-modal');
        const listContainer = document.getElementById('llm-endpoints-list');

        if (!modal || !listContainer) {
            console.error('LLM endpoints list modal not found');
            return;
        }

        try {
            const endpointsResult = await this.canisterClient.getLLMEndpoints();

            if (!endpointsResult || !('Ok' in endpointsResult)) {
                listContainer.innerHTML = '<div class="text-center text-red-500 py-4">Failed to load endpoints</div>';
                modal.classList.remove('hidden');
                return;
            }

            const endpoints: LLMEndpoint[] = endpointsResult.Ok;

            if (endpoints.length === 0) {
                listContainer.innerHTML = `
                    <div class="text-center text-gray-500 py-8">
                        <div class="w-12 h-12 mx-auto mb-2 opacity-50 flex items-center justify-center">
                            <i data-feather="server" class="w-12 h-12"></i>
                        </div>
                        <p>No LLM endpoints configured</p>
                        <p class="text-sm">Click "Add New Endpoint" to get started</p>
                    </div>`;
            } else {
                listContainer.innerHTML = endpoints.map(ep => `
                    <div class="p-4 bg-white border rounded-lg shadow-sm">
                        <div class="flex items-start justify-between">
                            <div class="flex-1">
                                <div class="flex items-center gap-2">
                                    <h3 class="font-semibold">${this.escapeHtml(ep.name)}</h3>
                                    <span class="px-2 py-0.5 text-xs rounded ${ep.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}">
                                        ${ep.enabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <span class="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">${this.escapeHtml(ep.backend_type)}</span>
                                </div>
                                <p class="text-sm text-gray-600 mt-1">${this.escapeHtml(ep.url)}</p>
                                <p class="text-sm text-gray-500">Model: ${this.escapeHtml(ep.model_name)} | Max tokens: ${ep.max_tokens} | Timeout: ${ep.timeout_seconds}s</p>
                            </div>
                            <div class="flex items-center gap-2 ml-4">
                                <button onclick="toggleLLMEndpoint('${this.escapeHtml(ep.name)}', ${!ep.enabled})"
                                        class="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded" title="${ep.enabled ? 'Disable' : 'Enable'}">
                                    <i data-feather="${ep.enabled ? 'pause-circle' : 'play-circle'}" class="w-5 h-5"></i>
                                </button>
                                <button onclick="editLLMEndpoint('${this.escapeHtml(ep.name)}')"
                                        class="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded" title="Edit">
                                    <i data-feather="edit-2" class="w-5 h-5"></i>
                                </button>
                                <button onclick="deleteLLMEndpoint('${this.escapeHtml(ep.name)}')"
                                        class="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                                    <i data-feather="trash-2" class="w-5 h-5"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                `).join('');
            }

            modal.classList.remove('hidden');

            // Re-init Feather icons
            if ((window as any).feather) {
                (window as any).feather.replace();
            }
        } catch (error) {
            console.error('Error loading LLM endpoints:', error);
            listContainer.innerHTML = '<div class="text-center text-red-500 py-4">Error loading endpoints</div>';
            modal.classList.remove('hidden');
        }
    }

    /**
     * Hide LLM endpoints list modal
     */
    public hideLLMEndpointsListModal(): void {
        const modal = document.getElementById('llm-endpoints-list-modal');
        if (modal) {
            modal.classList.add('hidden');
        }
    }

    /**
     * Edit an LLM endpoint
     */
    public async editLLMEndpoint(name: string): Promise<void> {
        try {
            const endpointsResult = await this.canisterClient.getLLMEndpoints();
            if (!endpointsResult || !('Ok' in endpointsResult)) return;

            const endpoint = endpointsResult.Ok.find((e: LLMEndpoint) => e.name === name);
            if (endpoint) {
                this.hideLLMEndpointsListModal();
                this.showAddLLMEndpointModal(endpoint);
            }
        } catch (error) {
            console.error('Error loading endpoint for edit:', error);
        }
    }

    /**
     * Delete an LLM endpoint
     */
    public async deleteLLMEndpoint(name: string): Promise<void> {
        if (!confirm(`Are you sure you want to delete the endpoint "${name}"?`)) {
            return;
        }

        try {
            const result = await this.canisterClient.removeLLMEndpoint(name);
            if (result && 'Ok' in result) {
                await this.showLLMEndpointsListModal(); // Refresh list
                await this.updateLLMStatus();
            } else {
                alert(`Failed to delete endpoint: ${result?.Err || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error deleting endpoint:', error);
            alert('Failed to delete endpoint');
        }
    }

    /**
     * Toggle LLM endpoint enabled/disabled
     */
    public async toggleLLMEndpoint(name: string, enabled: boolean): Promise<void> {
        try {
            const result = await this.canisterClient.enableLLMEndpoint(name, enabled);
            if (result && 'Ok' in result) {
                await this.showLLMEndpointsListModal(); // Refresh list
                await this.updateLLMStatus();
            } else {
                alert(`Failed to ${enabled ? 'enable' : 'disable'} endpoint: ${result?.Err || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error toggling endpoint:', error);
        }
    }

    /**
     * Escape HTML to prevent XSS
     */
    private escapeHtml(text: string): string {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}

/**
 * SettingsController class
 * Manages the settings page
 */
export class SettingsController extends BaseController {
    private aiBackendSettings: AIBackendSettingsController;

    constructor(services: ControllerServices) {
        super(services);
        this.aiBackendSettings = new AIBackendSettingsController(services);
    }

    getPageName(): PageName {
        return 'settings';
    }

    protected async doInit(): Promise<void> {
        // Load general settings via appCore
        const appCore = (window as any).appCore;
        if (appCore?.getModule('config')) {
            appCore.getModule('config').loadSettings();
        }

        // Initialize timezone settings
        if ((window as any).timezoneSettings) {
            (window as any).timezoneSettings.initialize();
        }

        // Initialize AI backend settings
        await this.aiBackendSettings.init();
    }

    /**
     * Get AI backend settings controller for external access
     */
    public getAIBackendSettings(): AIBackendSettingsController {
        return this.aiBackendSettings;
    }
}
